// ===== API Types =====

export interface ApiError {
  status: number;
  message: string;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
}

// ===== Auth Types =====

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: UserRole;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

// ===== User Types =====

export type UserRole =
  | 'super_admin'
  | 'broker_admin'
  | 'broker_user'
  | 'client'
  | 'employee'
  | 'consultant'
  | 'external_org';

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  name?: string;
  phone?: string;
  avatar?: string;
  broker_id?: string | null;
  org_id?: string | null;
  created_at?: string;
  updated_at?: string;
  /** Populated by GET /auth/me for super_admin sessions with an active impersonation cookie. */
  is_impersonating?: boolean;
  impersonated_broker_id?: string | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Broker = Record<string, any>;
export interface Lender {
  id: string;
  name: string;
  product_type: string | null;
  product_types: string[];
  prefill_enabled: number;
  is_active: number;
  status?: string;
  min_credit_score?: number | null;
  max_loan_amount?: number | null;
  min_loan_amount?: number | null;
  max_funding_amount?: number | null;
  contact_email?: string | null;
  lender_type?: string | null;
  submission_method?: string | null;
  field_map_count?: number;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}
export interface LenderProduct {
  id: string;
  lender_id: string;
  product_name: string;
  min_revenue: number | null;
  max_revenue: number | null;
  min_time_in_business: number | null;
  min_credit_score: number | null;
  excluded_industries: string | null;
  required_docs: string | null;
  max_amount: number | null;
  rate_range: string | null;
  term_range: string | null;
  enabled: number;
  created_at?: string;
  updated_at?: string;
}

// ── Lender Apps (doc_type-scoped PDF templates + field_map) ──
export interface LenderApp {
  id: string;
  name: string;
  doc_type: string | null;
  product_key: string | null;
  signature_required: number | null;
  amount_min_cents: number | null;
  amount_max_cents: number | null;
  has_template: boolean;
  /** Raw field_map JSON string (or null). The editor populates from this. */
  field_map?: string | null;
  field_map_keys: number;
  /** True when >1 active row shares this row's (lender_id, doc_type) — prefill uses newest. */
  duplicate?: boolean;
  created_at?: number;
  updated_at?: number;
}

export interface AcroFieldInfo {
  name: string;
  type: string;
}

export interface FieldMapDiff {
  mapped_ok: Array<{ key: string; field: string }>;
  mapped_missing: Array<{ key: string; field: string }>;
  unmapped_pdf_fields: AcroFieldInfo[];
}

export interface LenderAppTemplateResult {
  template_r2_key: string;
  extracted_fields: AcroFieldInfo[];
  field_map_diff: FieldMapDiff;
}

export interface LenderAppFieldsResult {
  has_template: boolean;
  extracted_fields: AcroFieldInfo[];
  field_map_diff: FieldMapDiff;
}

export interface ClientLenderMatchResult {
  client_id: string;
  lender_product_id: string;
  lender_id: string;
  lender_name: string;
  product_name: string;
  match_score: number;
  match_reasons: string[];
  rejection_reasons: string[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ClientSSOT = Record<string, any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AuditEntry = Record<string, any>;

export type DealStage = 'lead' | 'intake' | 'structuring' | 'credit_action' | 'docs_requested' | 'docs_received' | 'submission_ready' | 'submitted' | 'offers' | 'accepted' | 'funded' | 'invoiced' | 'closed' | 'declined' | 'withdrawn';

export interface NotificationPreference {
  id: string;
  user_id: string;
  event_type: string;
  channel_email: boolean;
  channel_sms: boolean;
  channel_in_app: boolean;
  channel_telegram: boolean;
  created_at: string;
  updated_at: string;
}

export interface NotificationProviderConfig {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'telegram' | 'webhook';
  is_enabled: boolean;
  config: string;
  created_at: string;
  updated_at: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Deal = Record<string, any>;

export interface DealOwner {
  id: string;
  client_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  ssn_last4: string | null;
  dob: string | null;
  ownership_percent: number;
  entity_name: string | null;
  entity_type: string | null;
  entity_ein: string | null;
  is_guarantor: number;
  is_primary_contact: number;
  title: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  avatar?: string;
}

// ===== Application Types =====

export type ApplicationStatus =
  | 'draft'
  | 'ready'
  | 'submitted'
  | 'under_review'
  | 'approved'
  | 'declined'
  | 'withdrawn';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Application = Record<string, any>;

export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  body?: string;
  read: boolean;
  created_at: string;
}

export interface FieldMapping {
  id: string;
  lender_id: string;
  pdf_field_name: string;
  ssot_fields: string[];
  separator: string;
  transform: string;
  field_type: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Document = Record<string, any>;

// ===== Command Center Types =====

export interface CommandSubmission {
  id: string;
  deal_id: string;
  lender_id: string;
  lender_name: string;
  status: 'pending' | 'under_review' | 'approved' | 'declined' | 'withdrawn';
  submitted_at: string;
  response_at: string | null;
  response_status: 'approved' | 'declined' | 'counter_offer' | 'more_info' | null;
  response_notes: string | null;
  pdf_r2_key: string | null;
  created_at: string;
  updated_at: string;
}

export interface CommandActivity {
  id: string;
  deal_id: string;
  action: string;
  details: string | null;
  created_at: string;
  created_by: string | null;
  user_name: string | null;
}

export interface CommandDoc {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  r2_key: string;
  category: string;
  is_verified: number;
  tile_status: string;
  created_at: string;
  original_filename: string | null;
  download_url: string;
}

export interface CommandPayload {
  deal: Deal;
  submissions: CommandSubmission[];
  activity: CommandActivity[];
  docs: CommandDoc[];
}

// ===== Document Extraction Types =====

export interface DailyBalance {
  date: string;
  balance: number;
}

export interface RawTransaction {
  date: string;
  description: string;
  amount: number;
  type: 'credit' | 'debit';
  balance: number;
}

export interface BankStatementFields {
  business_name: string | null;
  business_address: string | null;
  lender_name: string | null;
  account_number: string | null;
  statement_date_start: string | null;
  statement_date_end: string | null;
  ending_balance: number | null;
  average_ledger_balance: number | null;
  low_balance: number | null;
  high_balance: number | null;
  total_deposits: number | null;
  total_withdrawals: number | null;
  nsf_count: number;
  daily_balances: DailyBalance[];
  raw_transactions: RawTransaction[];
}

export interface ExtractionResult {
  status: 'not_extracted' | 'pending' | 'complete' | 'failed' | 'needs_review';
  id?: string;
  extracted_at?: string;
  extracted_by?: 'claude' | 'manual';
  confidence?: number;
  fields?: string;
  bank_statement_fields?: BankStatementFields;
}

export interface PendingExtraction {
  id: string;
  document_id: string;
  doc_type: string;
  status: string;
  file_name: string;
  category: string;
  deal_id: string | null;
  client_id: string | null;
  created_at: string;
}

export interface CrossDocValidation {
  consistent: boolean;
  mismatches: Array<{
    field: string;
    values: Array<{ doc_id: string; doc_name: string; value: string }>;
  }>;
}

// ===== Deal Document Types =====

export interface DealDocument {
  id: string;
  deal_id: string;
  client_id: string;
  broker_id: string;
  uploaded_by: string;
  file_name: string;
  file_type: string;
  file_size: number;
  r2_key: string;
  category: string;
  is_verified: number;
  created_at: string;
  download_url?: string;
}

export interface BulkUploadResult {
  uploaded: Array<{
    id: string;
    file_name: string;
    category: string;
    file_size: number;
    r2_key: string;
    created_at: string;
  }>;
  errors: Array<{ file_name: string; error: string }>;
  total: number;
  succeeded: number;
  failed: number;
}

export interface ZipUploadResult {
  uploaded: Array<{
    id: string;
    file_name: string;
    category: string;
    file_size: number;
    r2_key: string;
    auto_categorized: boolean;
    created_at: string;
  }>;
  errors: Array<{ file_name: string; error: string }>;
  total: number;
  succeeded: number;
  failed: number;
  auto_categorized: number;
  needs_review: number;
  zip_backup_key: string;
  summary: string;
}

// ===== Money Task (priority-scored) =====

export interface MoneyTask {
  id: string;
  deal_id: string;
  title: string;
  type: string;
  priority: string;
  status: string;
  due_at: string | null;
  deal_name: string | null;
  amount_requested: number | null;
  client_first_name: string | null;
  client_last_name: string | null;
  client_name: string | null;
  amount_bucket: number;
  urgency_score: number;
  is_overdue: number;
  created_at: string;
}

// ===== Query Params =====

export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  [key: string]: string | number | boolean | undefined;
}

// ===== API Client =====

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

class ApiClientError extends Error {
  status: number;
  errors?: Record<string, string[]>;

  constructor(status: number, message: string, errors?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.errors = errors;
  }
}

function buildQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  // Don't set Content-Type for FormData (browser sets boundary automatically).
  // Destructure to drop the key rather than using the forbidden delete operator.
  const extraHeaders = (options.headers as Record<string, string> | undefined) ?? {};
  const { 'Content-Type': _ignored, ...headersWithoutContentType } = extraHeaders;
  const headers: Record<string, string> = options.body instanceof FormData
    ? headersWithoutContentType
    : { 'Content-Type': 'application/json', ...extraHeaders };

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include', // send httpOnly cookie automatically
  });

  // The client portal (/portal, /portal2) is public — the linkId is the bearer credential
  // and every portal API is auth-free. A background 401 (e.g. a broker-session probe) must
  // NOT hijack the page to /login: that both breaks real clients opening their link directly
  // and blocks headless render/QA. Portal pages handle their own (public) data.
  // /start (the AllClear front door) is public too: every visitor is signed out, and the
  // mount-time /brokers/me probe was bouncing them all to /login (laptop walk 2026-11-03).
  if (
    response.status === 401 &&
    !path.startsWith('/auth/') &&
    window.location.pathname !== '/login' &&
    !window.location.pathname.startsWith('/portal') &&
    !window.location.pathname.startsWith('/start')
  ) {
    window.location.href = '/login';
    throw new ApiClientError(401, 'Unauthorized');
  }

  if (!response.ok) {
    let errorBody: { message?: string; error?: string; errors?: Record<string, string[]> } = {};
    try {
      errorBody = await response.json() as typeof errorBody;
    } catch {
      // Response body is not JSON
    }
    const rawError = errorBody.error ?? errorBody.message;
    const errorMsg = typeof rawError === 'string'
      ? rawError
      : rawError ? JSON.stringify(rawError) : `Request failed with status ${response.status}`;
    throw new ApiClientError(
      response.status,
      errorMsg,
      errorBody.errors,
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

// ===== API Methods =====

export const api = {
  // Auth
  auth: {
    async login(data: LoginRequest): Promise<LoginResponse> {
      const res = await request<{ success: boolean; data: LoginResponse }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.data;
    },

    register(data: RegisterRequest): Promise<LoginResponse> {
      return request<LoginResponse>('/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    forgotPassword(data: ForgotPasswordRequest): Promise<ApiResponse<null>> {
      return request<ApiResponse<null>>('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    resetPassword(data: ResetPasswordRequest): Promise<ApiResponse<null>> {
      return request<ApiResponse<null>>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async changeEmail(email: string): Promise<{ email: string }> {
      const res = await request<{ success: boolean; data: { email: string } }>('/auth/change-email', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      return res.data;
    },

    async me(): Promise<User> {
      const res = await request<{ success: boolean; data: User }>('/auth/me');
      return res.data;
    },

    async logout(): Promise<void> {
      await request<{ success: boolean }>('/auth/logout', {
        method: 'POST',
      });
    },

    refreshToken(): Promise<{ token: string }> {
      return request<{ token: string }>('/auth/refresh', {
        method: 'POST',
      });
    },
  },

  // Users (admin)
  users: {
    async list(params?: PaginationParams): Promise<{ success: boolean; data: User[]; meta: { page: number; limit: number; total: number; totalPages: number } }> {
      const qs = typeof params === 'object' ? buildQueryString(params ?? {}) : '';
      return request<{ success: boolean; data: User[]; meta: { page: number; limit: number; total: number; totalPages: number } }>(`/admin/users${qs}`);
    },

    async get(id: string): Promise<{ data: User }> {
      return request<{ data: User }>(`/admin/users/${id}`);
    },

    async create(data: { email: string; password: string; first_name: string; last_name: string; role: string }): Promise<User> {
      const res = await request<{ success: boolean; data: User }>('/admin/users', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.data;
    },

    async update(id: string, data: UpdateUserRequest & Record<string, unknown>): Promise<User> {
      const res = await request<{ success: boolean; data: User }>(`/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      return res.data;
    },

    delete(id: string): Promise<void> {
      return request<void>(`/admin/users/${id}`, {
        method: 'DELETE',
      });
    },
  },

  // ===== Convenience methods (used by route pages) =====

  // Admin
  async forgotPassword(email: string): Promise<void> {
    await api.auth.forgotPassword({ email });
  },

  async getAdminStats(): Promise<{ total_brokers: number; total_clients: number; total_deals: number; total_applications: number; pending_brokers: number; recent_activity: Array<{ id: string; user_email: string; action: string; resource_type: string; created_at: string; details?: string }> }> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/admin/stats');
    return { total_brokers: 0, total_clients: 0, total_deals: 0, total_applications: 0, pending_brokers: 0, recent_activity: [], ...res.data } as { total_brokers: number; total_clients: number; total_deals: number; total_applications: number; pending_brokers: number; recent_activity: Array<{ id: string; user_email: string; action: string; resource_type: string; created_at: string; details?: string }> };
  },
  async getAdminSettings(): Promise<Record<string, string>> {
    const res = await request<{ success: boolean; data: Record<string, string> }>('/admin/settings');
    return res.data;
  },
  async updateAdminSettings(data: Record<string, unknown>): Promise<Record<string, string>> {
    const res = await request<{ success: boolean; data: Record<string, string> }>('/admin/settings', { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async getBrokers(params?: unknown): Promise<{ data: Broker[]; totalPages: number; pagination: { page: number; limit: number; total: number } }> {
    const qs = typeof params === 'string' ? `?${params}` : buildQueryString((params ?? {}) as Record<string, string>);
    const res = await request<{ success: boolean; data: { brokers: Broker[]; pagination: { page: number; limit: number; total: number } } }>(`/admin/brokers${qs}`);
    const { brokers, pagination } = res.data;
    const totalPages = Math.ceil((pagination.total || 0) / (pagination.limit || 25));
    return { data: brokers ?? [], totalPages, pagination };
  },
  async getBroker(slug: string): Promise<Broker> {
    const res = await request<{ success: boolean; data: Broker }>(`/admin/brokers/${slug}`);
    return res.data;
  },
  async createBroker(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/admin/brokers', { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateBroker(slug: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/admin/brokers/${slug}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async approveBroker(slug: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/admin/brokers/${slug}/approve`, { method: 'POST' });
    return res.data;
  },
  async rejectBroker(slug: string): Promise<void> {
    await request<{ success: boolean }>(`/admin/brokers/${slug}`, { method: 'DELETE' });
  },
  async getLenders(params?: unknown): Promise<{ data: Lender[]; totalPages: number; pagination: { page: number; limit: number; total: number } }> {
    const qs = typeof params === 'string' ? `?${params}` : buildQueryString((params ?? {}) as Record<string, string>);
    const res = await request<{ success: boolean; data: { lenders: Lender[]; pagination: { page: number; limit: number; total: number } } }>(`/admin/lenders${qs}`);
    const { lenders, pagination } = res.data;
    const totalPages = Math.ceil((pagination.total || 0) / (pagination.limit || 25));
    return { data: lenders ?? [], totalPages, pagination };
  },
  async getLender(slug: string): Promise<Lender> {
    const res = await request<{ success: boolean; data: Lender }>(`/admin/lenders/${slug}`);
    return res.data;
  },
  async createLender(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/admin/lenders', { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateLender(slug: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/admin/lenders/${slug}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async uploadTemplate(slug: string, file: File): Promise<Record<string, unknown>> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/admin/lenders/${slug}/template`, { method: 'POST', body: formData });
    return res.data;
  },

  // ── Lender Apps (doc_type-scoped PDF templates + field_map) ──
  async getLenderApps(slug: string): Promise<LenderApp[]> {
    const res = await request<{ success: boolean; data: LenderApp[] }>(`/admin/lenders/${slug}/apps`);
    return res.data ?? [];
  },
  async createLenderApp(slug: string, data: Partial<LenderApp>): Promise<LenderApp> {
    const res = await request<{ success: boolean; data: LenderApp }>(`/admin/lenders/${slug}/apps`, { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateLenderApp(slug: string, appId: string, data: Partial<LenderApp>): Promise<LenderApp> {
    const res = await request<{ success: boolean; data: LenderApp }>(`/admin/lenders/${slug}/apps/${appId}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async deleteLenderApp(slug: string, appId: string): Promise<void> {
    await request<{ success: boolean }>(`/admin/lenders/${slug}/apps/${appId}`, { method: 'DELETE' });
  },
  async uploadLenderAppTemplate(slug: string, appId: string, file: File): Promise<LenderAppTemplateResult> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await request<{ success: boolean; data: LenderAppTemplateResult }>(`/admin/lenders/${slug}/apps/${appId}/template`, { method: 'POST', body: formData });
    return res.data;
  },
  async getLenderAppFields(slug: string, appId: string): Promise<LenderAppFieldsResult> {
    const res = await request<{ success: boolean; data: LenderAppFieldsResult }>(`/admin/lenders/${slug}/apps/${appId}/fields`);
    return res.data;
  },
  async getAllClients(params?: unknown): Promise<{ data: ClientSSOT[]; totalPages: number; pagination: { page: number; limit: number; total: number } }> {
    const qs = typeof params === 'string' ? `?${params}` : buildQueryString((params ?? {}) as Record<string, string>);
    const res = await request<{ success: boolean; data: { clients: ClientSSOT[]; pagination: { page: number; limit: number; total: number } } }>(`/admin/clients${qs}`);
    const { clients, pagination } = res.data;
    const totalPages = Math.ceil((pagination.total || 0) / (pagination.limit || 25));
    return { data: clients ?? [], totalPages, pagination };
  },
  async getAuditLog(params?: unknown): Promise<{ data: AuditEntry[]; totalPages: number; pagination: { page: number; limit: number; total: number } }> {
    const qs = typeof params === 'string' ? `?${params}` : buildQueryString((params ?? {}) as Record<string, string>);
    const res = await request<{ success: boolean; data: { logs: AuditEntry[]; pagination: { page: number; limit: number; total: number } } }>(`/admin/audit${qs}`);
    const { logs, pagination } = res.data;
    const totalPages = Math.ceil((pagination.total || 0) / (pagination.limit || 25));
    return { data: logs ?? [], totalPages, pagination };
  },
  async getNotifications(params?: unknown): Promise<{ data: Record<string, unknown>[]; totalPages: number; pagination: { page: number; limit: number; total: number } }> {
    const qs = typeof params === 'string' ? `?${params}` : buildQueryString((params ?? {}) as Record<string, string>);
    const res = await request<{ success: boolean; data: { notifications: Record<string, unknown>[]; pagination: { page: number; limit: number; total: number } } }>(`/admin/notifications${qs}`);
    const { notifications, pagination } = res.data;
    const totalPages = Math.ceil((pagination.total || 0) / (pagination.limit || 25));
    return { data: notifications ?? [], totalPages, pagination };
  },

  // Broker
  async getBrokerStats(): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/brokers/me/stats');
    return res.data;
  },
  async getBrokerDocRequests(): Promise<Record<string, unknown>[]> {
    const res = await request<{ success: boolean; data: Record<string, unknown>[] }>('/brokers/me/doc-requests');
    return res.data ?? [];
  },
  async getBrokerProfile(): Promise<Broker> {
    const res = await request<{ success: boolean; data: Broker }>('/brokers/me');
    return res.data;
  },
  async updateBrokerProfile(data: Record<string, unknown>): Promise<Broker> {
    const res = await request<{ success: boolean; data: Broker }>('/brokers/me', { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async getIntakeSchema(): Promise<unknown[] | null> {
    const res = await request<{ success: boolean; data: { schema: unknown[] | null } }>('/brokers/me/intake-schema');
    return res.data.schema;
  },
  async updateIntakeSchema(schema: unknown[]): Promise<void> {
    await request<{ success: boolean }>('/brokers/me/intake-schema', { method: 'PUT', body: JSON.stringify({ schema }) });
  },
  async getClients(params?: unknown): Promise<{ data: ClientSSOT[]; totalPages: number; pagination: { page: number; limit: number; total: number } }> {
    const qs = typeof params === 'string' ? `?${params}` : buildQueryString((params ?? {}) as Record<string, string>);
    const res = await request<{ success: boolean; data: { clients: ClientSSOT[]; pagination: { page: number; limit: number; total: number } } }>(`/brokers/me/clients${qs}`);
    const { clients, pagination } = res.data;
    const totalPages = Math.ceil((pagination.total || 0) / (pagination.limit || 25));
    return { data: clients ?? [], totalPages, pagination };
  },
  async getClient(id: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/clients/${id}`);
    return res.data;
  },
  async createClient(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/brokers/me/clients', { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateClient(id: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/clients/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async deleteClient(clientId: string): Promise<{ message: string; id: string }> {
    const res = await request<{ success: boolean; data: { message: string; id: string } }>(`/brokers/me/clients/${clientId}`, { method: 'DELETE' });
    return res.data;
  },
  async restoreClient(clientId: string): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/clients/${clientId}/restore`, { method: 'POST' });
  },
  async getTrashedClients(): Promise<ClientSSOT[]> {
    const res = await request<{ success: boolean; data: ClientSSOT[] }>('/brokers/me/clients/trash/list');
    return res.data ?? [];
  },
  async getClientDeals(clientId: string): Promise<Deal[]> {
    const res = await request<{ success: boolean; data: Deal[] }>(`/brokers/me/clients/${clientId}/deals`);
    return res.data ?? [];
  },
  async getClientDocumentsBroker(clientId: string): Promise<Array<{ id: string; client_id: string; doc_type: string; original_filename: string; stored_filename: string; r2_key: string; file_size: number; uploaded_at: string; download_url: string }>> {
    const res = await request<{ success: boolean; data: Array<{ id: string; client_id: string; doc_type: string; original_filename: string; stored_filename: string; r2_key: string; file_size: number; uploaded_at: string; download_url: string }> }>(`/brokers/me/clients/${clientId}/documents`);
    return res.data ?? [];
  },
  async uploadClientDocument(clientId: string, file: File, docType: string): Promise<Record<string, unknown>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    const res = await request<Record<string, unknown>>(`/brokers/me/clients/${clientId}/documents`, { method: 'POST', body: formData });
    return res;
  },
  async initMultipartUpload(clientId: string, filename: string, fileSize: number, docType: string, contentType?: string): Promise<{ uploadId: string; r2Key: string }> {
    const res = await request<{ success: boolean; data: { uploadId: string; r2Key: string } }>(`/brokers/me/clients/${clientId}/documents/multipart/init`, {
      method: 'POST',
      body: JSON.stringify({ filename, fileSize, docType, contentType }),
    });
    return res.data;
  },
  async uploadPart(clientId: string, uploadId: string, partNumber: number, chunk: ArrayBuffer): Promise<{ partNumber: number; etag: string }> {
    const response = await fetch(`${(import.meta.env.VITE_API_URL ?? '/api')}/brokers/me/clients/${clientId}/documents/multipart/${uploadId}/part?partNumber=${partNumber}`, {
      method: 'POST',
      body: chunk,
      credentials: 'include',
    });
    if (!response.ok) throw new Error(`Part upload failed (${response.status})`);
    const json = await response.json() as { success: boolean; data: { partNumber: number; etag: string } };
    return json.data;
  },
  async completeMultipartUpload(clientId: string, uploadId: string, parts: Array<{ partNumber: number; etag: string }>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/clients/${clientId}/documents/multipart/${uploadId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ parts }),
    });
    return res.data;
  },
  async deleteClientDocument(clientId: string, docId: string): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/clients/${clientId}/documents/${docId}`, { method: 'DELETE' });
  },
  async updateClientDocumentType(clientId: string, docId: string, docType: string): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/clients/${clientId}/documents/${docId}`, { method: 'PATCH', body: JSON.stringify({ doc_type: docType }) });
  },
  async reassignClientDocument(clientId: string, docId: string, updates: { doc_type?: string; bank_name?: string | null }): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/clients/${clientId}/documents/${docId}`, { method: 'PATCH', body: JSON.stringify(updates) });
  },
  async getDocPreviews(clientId: string, docIds: string[]): Promise<Array<{
    id: string; file_name: string; file_type: string; file_size: number;
    page_count: number | null; width: number | null; height: number | null;
    orientation: number; file_hash: string | null;
    download_url: string; thumbnail_url: string | null;
  }>> {
    const res = await request<{ success: boolean; data: Array<Record<string, unknown>> }>(`/brokers/me/clients/${clientId}/documents/previews`, { method: 'POST', body: JSON.stringify({ doc_ids: docIds }) });
    return res.data as any;
  },
  async bulkDeleteClientDocuments(clientId: string, docIds: string[]): Promise<{ deleted: number }> {
    const res = await request<{ success: boolean; data: { deleted: number } }>(`/brokers/me/clients/${clientId}/documents/bulk-delete`, { method: 'POST', body: JSON.stringify({ doc_ids: docIds }) });
    return res.data;
  },
  async deleteAllClientDocuments(clientId: string): Promise<{ deleted: number }> {
    const res = await request<{ success: boolean; data: { deleted: number } }>(`/brokers/me/clients/${clientId}/documents`, { method: 'DELETE' });
    return res.data;
  },

  // ── Client Owners ──
  async getClientOwners(clientId: string): Promise<DealOwner[]> {
    const res = await request<{ success: boolean; data: DealOwner[] }>(`/brokers/me/clients/${clientId}/owners`);
    return res.data ?? [];
  },
  async createClientOwner(clientId: string, data: Partial<DealOwner>): Promise<DealOwner> {
    const res = await request<{ success: boolean; data: DealOwner }>(`/brokers/me/clients/${clientId}/owners`, { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateClientOwner(clientId: string, ownerId: string, data: Partial<DealOwner>): Promise<DealOwner> {
    const res = await request<{ success: boolean; data: DealOwner }>(`/brokers/me/clients/${clientId}/owners/${ownerId}`, { method: 'PUT', body: JSON.stringify(data) });
    return res.data;
  },
  async deleteClientOwner(clientId: string, ownerId: string): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/clients/${clientId}/owners/${ownerId}`, { method: 'DELETE' });
  },
  async reorderClientOwners(clientId: string, ownerIds: string[]): Promise<DealOwner[]> {
    const res = await request<{ success: boolean; data: DealOwner[] }>(`/brokers/me/clients/${clientId}/owners/reorder`, { method: 'PATCH', body: JSON.stringify({ owner_ids: ownerIds }) });
    return res.data ?? [];
  },

  // ── Agreements ──
  async getClientAgreements(clientId: string): Promise<Array<{ id: string; fee_percent: number; file_name: string; file_size: number; created_at: string }>> {
    const res = await request<{ success: boolean; data: Array<{ id: string; fee_percent: number; file_name: string; file_size: number; created_at: string }> }>(`/brokers/me/clients/${clientId}/agreements`);
    return res.data ?? [];
  },
  async generateAgreement(clientId: string, feePercent: number): Promise<{ id: string; fee_percent: number; file_name: string; file_size: number; created_at: string }> {
    const res = await request<{ success: boolean; data: { id: string; fee_percent: number; file_name: string; file_size: number; created_at: string } }>(`/brokers/me/clients/${clientId}/agreements`, {
      method: 'POST',
      body: JSON.stringify({ fee_percent: feePercent }),
    });
    return res.data;
  },
  async getAgreementSignedUrl(clientId: string, agreementId: string): Promise<{ url: string }> {
    return request(`/brokers/me/clients/${clientId}/agreements/${agreementId}/download`);
  },

  // ── Client Full Page ──
  async getClientFull(clientId: string): Promise<{
    client: Record<string, unknown>;
    organizations: Record<string, unknown>[];
    route_steps: Record<string, unknown>[];
    communications: Record<string, unknown>[];
    timeline: Array<{ type: string; action: string; timestamp: string; details: string | null; channel?: string; direction?: string }>;
    documents: Record<string, unknown>[];
    extractions: Record<string, unknown>[];
    deals: Record<string, unknown>[];
    result: { funded: number | null; commission: number | null; approvals: number; total_submissions: number };
  }> {
    const res = await request<{ success: true; data: {
      client: Record<string, unknown>;
      organizations: Record<string, unknown>[];
      route_steps: Record<string, unknown>[];
      communications: Record<string, unknown>[];
      timeline: Array<{ type: string; action: string; timestamp: string; details: string | null; channel?: string; direction?: string }>;
      documents: Record<string, unknown>[];
      extractions: Record<string, unknown>[];
      deals: Record<string, unknown>[];
      result: { funded: number | null; commission: number | null; approvals: number; total_submissions: number };
    } }>(`/brokers/me/clients/${clientId}/full`);
    return res.data;
  },

  // ── Client Organizations ──
  async createOrganization(clientId: string, data: { name: string; ein?: string; is_primary?: boolean }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/clients/${clientId}/organizations`, { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },

  // ── Route Steps ──
  async getRouteSteps(clientId: string, organizationId?: string): Promise<Record<string, unknown>[]> {
    const qs = organizationId ? `?organization_id=${organizationId}` : '';
    const res = await request<{ success: boolean; data: Record<string, unknown>[] }>(`/brokers/me/clients/${clientId}/route-steps${qs}`);
    return res.data ?? [];
  },
  async createRouteStep(clientId: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/clients/${clientId}/route-steps`, { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateRouteStep(clientId: string, stepId: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/clients/${clientId}/route-steps/${stepId}`, { method: 'PUT', body: JSON.stringify(data) });
    return res.data;
  },
  async deleteRouteStep(clientId: string, stepId: string): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/clients/${clientId}/route-steps/${stepId}`, { method: 'DELETE' });
  },
  async reorderRouteSteps(clientId: string, stepIds: string[]): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/clients/${clientId}/route-steps/reorder`, { method: 'PATCH', body: JSON.stringify({ step_ids: stepIds }) });
  },
  async sendDocsToStep(clientId: string, stepId: string, documentIds: string[]): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/clients/${clientId}/route-steps/${stepId}/send-docs`, { method: 'POST', body: JSON.stringify({ document_ids: documentIds }) });
  },
  async logBankerContact(clientId: string, stepId: string, data: { contact_type: 'call' | 'email' | 'text'; notes?: string }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/clients/${clientId}/route-steps/${stepId}/log-banker-contact`, { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async getSpeedInsights(): Promise<{ lenders: Array<{ lender_id: string; name: string; avg_days: number; fastest: number; slowest: number; total_decisions: number }> }> {
    const res = await request<{ success: boolean; data: { lenders: Array<{ lender_id: string; name: string; avg_days: number; fastest: number; slowest: number; total_decisions: number }> } }>('/brokers/me/clients/insights/speed');
    return res.data;
  },

  // ── Deal Outcomes (Win/Loss Tracker) ──
  async recordDealOutcome(data: {
    route_step_id: string;
    outcome: 'approved' | 'declined' | 'withdrawn';
    decline_reason?: string;
    decline_category?: string;
    amount_requested?: number;
    amount_approved?: number;
    notes?: string;
  }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/brokers/me/deal-outcomes', { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async recordFunded(data: {
    route_step_id: string;
    client_id: string;
    funded_amount?: number;
    funded_date?: string;
    commission_rate?: number;
    notes?: string;
  }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/brokers/me/deal-outcomes/funded', { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async getLenderInsights(lenderId?: string): Promise<Record<string, unknown>[]> {
    const qs = lenderId ? `?lender_id=${lenderId}` : '';
    const res = await request<{ success: boolean; data: Record<string, unknown>[] }>(`/brokers/me/deal-outcomes/insights/lenders${qs}`);
    return res.data ?? [];
  },

  // ── Client Communications ──
  async getClientCommunications(clientId: string, channel?: string, page?: number): Promise<{ data: Record<string, unknown>[]; total: number }> {
    const params: Record<string, string> = {};
    if (channel) params.channel = channel;
    if (page) params.page = String(page);
    const qs = buildQueryString(params);
    const res = await request<{ success: boolean; data: Record<string, unknown>[]; total: number }>(`/brokers/me/clients/${clientId}/communications${qs}`);
    return { data: res.data ?? [], total: res.total ?? 0 };
  },
  async createCommunication(clientId: string, data: { channel: string; direction: string; subject?: string; content: string; attachments?: string[]; comm_timestamp?: string }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/clients/${clientId}/communications`, { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async deleteCommunication(clientId: string, commId: string): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/clients/${clientId}/communications/${commId}`, { method: 'DELETE' });
  },

  // ── Route Templates ──
  async getRouteTemplates(): Promise<Record<string, unknown>[]> {
    const res = await request<{ success: boolean; data: Record<string, unknown>[] }>('/brokers/me/route-templates');
    return res.data ?? [];
  },
  async createRouteTemplate(name: string, clientId: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/brokers/me/route-templates', { method: 'POST', body: JSON.stringify({ name, client_id: clientId }) });
    return res.data;
  },
  async applyRouteTemplate(clientId: string, templateId: string): Promise<{ steps_created: number }> {
    const res = await request<{ success: boolean; data: { message: string; steps_created: number } }>(`/brokers/me/clients/${clientId}/apply-template/${templateId}`, { method: 'POST' });
    return res.data;
  },
  async deleteRouteTemplate(templateId: string): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/route-templates/${templateId}`, { method: 'DELETE' });
  },

  async getDeals(params?: unknown): Promise<{ data: Deal[]; totalPages: number; pagination: { page: number; limit: number; total: number } }> {
    const qs = typeof params === 'string' ? `?${params}` : buildQueryString((params ?? {}) as Record<string, string>);
    const res = await request<{ success: boolean; data: { deals: Deal[]; pagination: { page: number; limit: number; total: number } } }>(`/brokers/me/deals${qs}`);
    const { deals, pagination } = res.data;
    const totalPages = Math.ceil((pagination.total || 0) / (pagination.limit || 25));
    return { data: deals ?? [], totalPages, pagination };
  },
  async getDeal(slug: string): Promise<Deal> {
    const res = await request<{ success: boolean; data: Deal }>(`/brokers/me/deals/${slug}`);
    return res.data;
  },
  async createDeal(data: Record<string, unknown>): Promise<Deal> {
    const res = await request<{ success: boolean; data: Deal }>('/brokers/me/deals', { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async selectLender(dealId: string, data: { lender_name: string; product_name: string }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/deals/${dealId}/select-lender`, { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateDeal(slug: string, data: Record<string, unknown>): Promise<Deal> {
    const res = await request<{ success: boolean; data: Deal }>(`/brokers/me/deals/${slug}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async deleteDeal(slug: string): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/deals/${slug}`, { method: 'DELETE' });
  },
  async getDealApplications(slug: string): Promise<Application[]> {
    const res = await request<{ success: boolean; data: Application[] }>(`/brokers/me/deals/${slug}/applications`);
    return res.data ?? [];
  },
  async deleteApplication(slug: string): Promise<void> {
    await request<{ success: boolean }>(`/applications/${slug}`, { method: 'DELETE' });
  },
  async updateApplication(slug: string, updates: Record<string, unknown>): Promise<void> {
    await request<{ success: boolean }>(`/applications/${slug}`, { method: 'PATCH', body: JSON.stringify(updates) });
  },
  async getApplications(params?: unknown): Promise<Record<string, unknown>> {
    const qs = typeof params === 'string' ? `?${params}` : buildQueryString((params ?? {}) as Record<string, string>);
    const res = await request<{ success: boolean; data: { applications: unknown[]; pagination: { page: number; limit: number; total: number } } }>(`/brokers/me/applications${qs}`);
    const { applications, pagination } = res.data;
    const totalPages = Math.ceil((pagination.total || 0) / (pagination.limit || 25));
    return { data: applications ?? [], totalPages, pagination };
  },

  // Client portal
  async getClientProfile(): Promise<ClientSSOT> {
    const res = await request<{ success: boolean; data: ClientSSOT }>('/clients/me');
    return res.data;
  },
  async updateClientProfile(data: Record<string, unknown>): Promise<ClientSSOT> {
    const res = await request<{ success: boolean; data: ClientSSOT }>('/clients/me', { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async getClientIntake(): Promise<ClientSSOT> {
    const res = await request<{ success: boolean; data: ClientSSOT }>('/clients/me');
    return res.data;
  },
  async updateClientIntake(data: Record<string, unknown>): Promise<ClientSSOT> {
    const res = await request<{ success: boolean; data: ClientSSOT }>('/clients/me', { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async getClientDealsPortal(): Promise<Deal[]> {
    const res = await request<{ success: boolean; data: Deal[] }>('/clients/me/deals');
    return res.data ?? [];
  },
  async getClientDeal(slug: string): Promise<Deal> {
    const res = await request<{ success: boolean; data: Deal }>(`/clients/me/deals/${slug}`);
    return res.data;
  },
  async getClientDealApplications(slug: string): Promise<Application[]> {
    const res = await request<{ success: boolean; data: Application[] }>(`/clients/me/deals/${slug}/applications`);
    return res.data ?? [];
  },
  async getClientDocuments(): Promise<Document[]> {
    const res = await request<{ success: boolean; data: Document[] }>('/clients/me/documents');
    return res.data ?? [];
  },
  async uploadDocument(file: File, name: string): Promise<Record<string, unknown>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/clients/me/documents', { method: 'POST', body: formData });
    return res.data;
  },
  async deleteDocument(id: string): Promise<void> {
    await request<{ success: boolean }>(`/documents/${id}`, { method: 'DELETE' });
  },
  async markNotificationRead(id: string): Promise<void> {
    await request<{ success: boolean }>(`/notifications/${id}/read`, { method: 'PATCH' });
  },

  // Notification Preferences
  async getNotificationPreferences(): Promise<NotificationPreference[]> {
    const res = await request<{ success: boolean; data: NotificationPreference[] }>('/notifications/preferences');
    return res.data ?? [];
  },
  async updateNotificationPreferences(preferences: NotificationPreference[]): Promise<NotificationPreference[]> {
    const res = await request<{ success: boolean; data: NotificationPreference[] }>('/notifications/preferences', {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    });
    return res.data ?? [];
  },

  // Notification Settings (admin)
  async getNotificationSettings(): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/admin/notification-settings');
    return res.data;
  },
  async updateNotificationSettings(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/admin/notification-settings', { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },

  // Notification Providers (admin)
  async getNotificationProviders(): Promise<NotificationProviderConfig[]> {
    const res = await request<{ success: boolean; data: NotificationProviderConfig[] }>('/admin/notification-providers');
    return res.data ?? [];
  },
  async createNotificationProvider(data: { name: string; type: string; config: Record<string, string> }): Promise<NotificationProviderConfig> {
    const res = await request<{ success: boolean; data: NotificationProviderConfig }>('/admin/notification-providers', { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateNotificationProvider(id: string, data: Record<string, unknown>): Promise<NotificationProviderConfig> {
    const res = await request<{ success: boolean; data: NotificationProviderConfig }>(`/admin/notification-providers/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async deleteNotificationProvider(id: string): Promise<void> {
    await request<{ success: boolean }>(`/admin/notification-providers/${id}`, { method: 'DELETE' });
  },
  async testNotificationProvider(id: string): Promise<{ test_result: string; error: string | null }> {
    const res = await request<{ success: boolean; data: { test_result: string; error: string | null } }>(`/admin/notification-providers/${id}/test`, { method: 'POST' });
    return res.data;
  },
  // Masquerade (admin only)
  async masquerade(userId: string): Promise<{ token: string; user: User }> {
    const res = await request<{ success: boolean; data: { token: string; user: User } }>(`/admin/masquerade/${userId}`, {
      method: 'POST',
    });
    return res.data;
  },

  // Broker impersonation (super_admin only)
  async impersonateBroker(brokerId: string): Promise<{ broker_id: string; company_name: string; slug: string }> {
    const res = await request<{ success: boolean; data: { broker_id: string; company_name: string; slug: string } }>(`/admin/brokers/${brokerId}/impersonate`);
    return res.data;
  },
  async stopImpersonateBroker(brokerId: string): Promise<void> {
    await request<{ success: boolean }>(`/admin/brokers/${brokerId}/impersonate`, { method: 'DELETE' });
  },

  /** Clear an active broker impersonation using the id from GET /auth/me.
   *  The /:id segment must be non-empty for the route to match, even though
   *  the handler only checks role + deletes the cookie. */
  async stopImpersonation(brokerId: string): Promise<void> {
    await request<{ success: boolean }>(`/admin/brokers/${brokerId}/impersonate`, { method: 'DELETE' });
  },

  // Lender matching
  async getLenderMatches(dealId: string): Promise<unknown[]> {
    const res = await request<{ success: boolean; data: unknown[] }>(`/brokers/me/deals/${dealId}/lender-matches`);
    return res.data ?? [];
  },
  async refreshLenderMatches(dealId: string): Promise<unknown[]> {
    const res = await request<{ success: boolean; data: unknown[] }>(`/brokers/me/deals/${dealId}/lender-matches/refresh`, { method: 'POST' });
    return res.data ?? [];
  },

  // Lender products (admin)
  async getLenderProducts(lenderId: string): Promise<LenderProduct[]> {
    const res = await request<{ success: boolean; data: LenderProduct[] }>(`/admin/lenders/${lenderId}/products`);
    return res.data ?? [];
  },
  async createLenderProduct(lenderId: string, data: Partial<LenderProduct>): Promise<LenderProduct> {
    const res = await request<{ success: boolean; data: LenderProduct }>(`/admin/lenders/${lenderId}/products`, { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateLenderProduct(lenderId: string, productId: string, data: Partial<LenderProduct>): Promise<LenderProduct> {
    const res = await request<{ success: boolean; data: LenderProduct }>(`/admin/lenders/${lenderId}/products/${productId}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async deleteLenderProduct(lenderId: string, productId: string): Promise<void> {
    await request<{ success: boolean }>(`/admin/lenders/${lenderId}/products/${productId}`, { method: 'DELETE' });
  },

  // Client lender matches (broker)
  async getClientMatches(clientId: string): Promise<ClientLenderMatchResult[]> {
    const res = await request<{ success: boolean; data: ClientLenderMatchResult[] }>(`/brokers/me/clients/${clientId}/matches`);
    return res.data ?? [];
  },
  async calculateClientMatches(clientId: string): Promise<ClientLenderMatchResult[]> {
    const res = await request<{ success: boolean; data: ClientLenderMatchResult[] }>(`/brokers/me/clients/${clientId}/calculate-matches`, { method: 'POST' });
    return res.data ?? [];
  },

  // Bank statement analysis
  async analyzeBankStatement(documentId: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/documents/${documentId}/analyze`, { method: 'POST' });
    return res.data;
  },
  async getBankAnalysis(documentId: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/documents/${documentId}/analysis`);
    return res.data;
  },
  async getClientBankAnalyses(clientId: string): Promise<unknown[]> {
    const res = await request<{ success: boolean; data: unknown[] }>(`/brokers/me/clients/${clientId}/bank-analyses`);
    return res.data ?? [];
  },

  // Deal messages
  async getDealMessages(dealId: string): Promise<unknown[]> {
    const res = await request<{ success: boolean; data: unknown }>(`/deals/${dealId}/messages`);
    // API may return { data: { messages: [], pagination } } or { data: [] }
    const raw = res.data;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === 'object' && 'messages' in raw && Array.isArray((raw as any).messages)) {
      return (raw as any).messages;
    }
    return [];
  },
  async sendDealMessage(dealId: string, body: string, channel: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/deals/${dealId}/messages`, { method: 'POST', body: JSON.stringify({ body, channel }) });
    return res.data;
  },
  async getUnreadMessageCount(dealId: string): Promise<number> {
    const res = await request<{ success: boolean; data: { count: number } }>(`/deals/${dealId}/messages/unread-count`);
    return res.data?.count ?? 0;
  },
  async markMessagesRead(dealId: string): Promise<void> {
    await request<{ success: boolean }>(`/deals/${dealId}/messages/mark-read`, { method: 'POST' });
  },

  // Brand
  async getBrand(subdomain?: string): Promise<Record<string, unknown>> {
    const qs = subdomain ? `?subdomain=${subdomain}` : '';
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brand${qs}`);
    return res.data;
  },
  async getBrokerBrand(): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/brokers/me/brand');
    return res.data;
  },
  async updateBrokerBrand(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/brokers/me/brand', { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async uploadBrandLogo(file: File): Promise<Record<string, unknown>> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/brokers/me/brand/logo', { method: 'POST', body: formData });
    return res.data;
  },
  async getApplyLink(): Promise<string> {
    const res = await request<{ success: boolean; data: { url: string } }>('/brokers/me/apply-link');
    return res.data?.url ?? '';
  },

  // Public intake
  async submitPublicIntake(slug: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>("/public/intake", { method: 'POST', body: JSON.stringify({ ...data, broker_slug: slug }) });
    return res.data;
  },

  // Intake link & submissions (broker)
  async getIntakeLink(): Promise<{ intake_url: string; slug: string }> {
    const res = await request<{ success: boolean; data: { intake_url: string; slug: string } }>('/brokers/me/intake-link');
    return res.data;
  },
  async regenerateIntakeLink(): Promise<{ intake_url: string; slug: string }> {
    const res = await request<{ success: boolean; data: { intake_url: string; slug: string } }>('/brokers/me/intake-link/regenerate', { method: 'POST' });
    return res.data;
  },
  async getIntakeSubmissions(status?: string): Promise<Record<string, unknown>[]> {
    const qs = status ? `?status=${status}` : '';
    const res = await request<{ success: boolean; data: Record<string, unknown>[] }>(`/brokers/me/intake-submissions${qs}`);
    return res.data ?? [];
  },
  async getIntakeSubmissionCount(): Promise<number> {
    const res = await request<{ success: boolean; data: { count: number } }>('/brokers/me/intake-submissions/count');
    return res.data?.count ?? 0;
  },

  // Tasks
  async getTasks(params?: Record<string, string>): Promise<Record<string, unknown>> {
    const qs = buildQueryString((params ?? {}) as Record<string, string>);
    const res = await request<{ success: boolean; data: { tasks: unknown[]; pagination: { page: number; limit: number; total: number } } }>(`/tasks${qs}`);
    const { tasks, pagination } = res.data;
    return { data: tasks ?? [], pagination };
  },
  async createTask(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/tasks', { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateTask(id: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async completeTask(id: string, outcome?: string): Promise<{ transition?: { next_task?: Record<string, unknown>; deal_stage?: string; deal_closed?: boolean; escalated?: boolean } }> {
    const body = outcome ? JSON.stringify({ outcome }) : undefined;
    return await request<{ success: boolean; transition?: { next_task?: Record<string, unknown>; deal_stage?: string; deal_closed?: boolean; escalated?: boolean } }>(`/tasks/${id}/complete`, { method: 'PUT', body });
  },
  async getStuckDeals(): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: { deals: unknown[] } }>('/brokers/me/deals/stuck');
    return { deals: res.data?.deals ?? [] };
  },
  async deleteTask(id: string): Promise<void> {
    await request<{ success: boolean }>(`/tasks/${id}`, { method: 'DELETE' });
  },
  async getOverdueTaskCount(): Promise<number> {
    const res = await request<{ success: boolean; data: { count: number } }>('/tasks/overdue-count');
    return res.data?.count ?? 0;
  },
  async getMoneyTasks(): Promise<MoneyTask[]> {
    const res = await request<{ success: boolean; data: { tasks: MoneyTask[] } }>('/tasks/money');
    return res.data?.tasks ?? [];
  },

  // Notes
  async getDealNotes(dealId: string): Promise<unknown[]> {
    const res = await request<{ success: boolean; data: unknown[] }>(`/deals/${dealId}/notes`);
    return res.data ?? [];
  },
  async createDealNote(dealId: string, body: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/deals/${dealId}/notes`, { method: 'POST', body: JSON.stringify({ body }) });
    return res.data;
  },
  async updateDealNote(dealId: string, noteId: string, body: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/deals/${dealId}/notes/${noteId}`, { method: 'PATCH', body: JSON.stringify({ body }) });
    return res.data;
  },
  async deleteDealNote(dealId: string, noteId: string): Promise<void> {
    await request<{ success: boolean }>(`/deals/${dealId}/notes/${noteId}`, { method: 'DELETE' });
  },
  async toggleNotePin(dealId: string, noteId: string): Promise<void> {
    await request<{ success: boolean }>(`/deals/${dealId}/notes/${noteId}/pin`, { method: 'PATCH' });
  },
  async getUnreadMentionCount(): Promise<number> {
    const res = await request<{ success: boolean; data: { count: number } }>('/notes/mentions/unread-count');
    return res.data?.count ?? 0;
  },

  // Offers
  async getDealOffers(dealId: string): Promise<unknown[]> {
    const res = await request<{ success: boolean; data: unknown[] }>(`/brokers/me/deals/${dealId}/offers`);
    return res.data ?? [];
  },
  async createOffer(dealId: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/deals/${dealId}/offers`, { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateOffer(dealId: string, offerId: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/deals/${dealId}/offers/${offerId}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async acceptOffer(dealId: string, offerId: string): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/deals/${dealId}/offers/${offerId}/accept`, { method: 'POST' });
  },
  async createShareLink(dealId: string): Promise<string> {
    const res = await request<{ success: boolean; data: { url: string } }>(`/brokers/me/deals/${dealId}/offers/share-link`, { method: 'POST' });
    return res.data?.url ?? '';
  },

  // Enforcement
  async getPreSubmitStatus(dealId: string): Promise<{
    docs: Array<{ doc_type: string; label: string; is_required: boolean; is_verified: boolean; expires_at: string | null; expired: boolean }>;
    channel: { confirmed: boolean; rm_name: string | null; rm_email: string | null; existing_relationship: number | null };
    readiness_score: number | null;
    blockers: string[];
    can_submit: boolean;
  }> {
    const res = await request<{ success: boolean; data: { docs: Array<{ doc_type: string; label: string; is_required: boolean; is_verified: boolean; expires_at: string | null; expired: boolean }>; channel: { confirmed: boolean; rm_name: string | null; rm_email: string | null; existing_relationship: number | null }; readiness_score: number | null; blockers: string[]; can_submit: boolean } }>(`/brokers/me/deals/${dealId}/pre-submit-status`);
    return res.data;
  },
  async submitDeal(dealId: string): Promise<{ submitted: boolean; application_id: string }> {
    const res = await request<{ success: boolean; data: { submitted: boolean; application_id: string } }>(`/brokers/me/deals/${dealId}/submit`, { method: 'POST' });
    return res.data;
  },

  // Checklist
  async getDealChecklist(dealId: string): Promise<unknown[]> {
    const res = await request<{ success: boolean; data: unknown[] }>(`/brokers/me/deals/${dealId}/checklist`);
    return res.data ?? [];
  },
  async generateChecklist(dealId: string): Promise<unknown[]> {
    const res = await request<{ success: boolean; data: unknown[] }>(`/brokers/me/deals/${dealId}/checklist/generate`, { method: 'POST' });
    return res.data ?? [];
  },
  async getChecklistStatus(dealId: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/deals/${dealId}/checklist/status`);
    return res.data;
  },
  async updateChecklistItem(dealId: string, itemId: string, data: Record<string, unknown>): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/deals/${dealId}/checklist/${itemId}`, { method: 'PATCH', body: JSON.stringify(data) });
  },

  // Search
  async search(query: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/search?q=${encodeURIComponent(query)}`);
    return res.data;
  },

  // AI
  async generateDealMemo(dealId: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/deals/${dealId}/generate-memo`, { method: 'POST' });
    return res.data;
  },
  async analyzeDecline(applicationId: string, declineText: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/applications/${applicationId}/analyze-decline`, { method: 'POST', body: JSON.stringify({ declineText }) });
    return res.data;
  },
  async chatIntake(slug: string, conversationHistory: unknown[], clientId?: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/clients/intake/chat', { method: 'POST', body: JSON.stringify({ conversationHistory, clientId, brokerSlug: slug }) });
    return res.data;
  },

  // Admin - Document Requirements
  async getDocumentRequirements(): Promise<unknown[]> {
    const res = await request<{ success: boolean; data: unknown[] }>('/admin/document-requirements');
    return res.data ?? [];
  },
  async createDocumentRequirement(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/admin/document-requirements', { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateDocumentRequirement(id: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/admin/document-requirements/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async deleteDocumentRequirement(id: string): Promise<void> {
    await request<{ success: boolean }>(`/admin/document-requirements/${id}`, { method: 'DELETE' });
  },
  async getPiiAccessLogs(params?: Record<string, string>): Promise<Record<string, unknown>> {
    const qs = buildQueryString((params ?? {}) as Record<string, string>);
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/compliance/pii-access-logs${qs}`);
    return res.data;
  },

  // Analytics
  async getAnalyticsPipeline(period?: string): Promise<Record<string, unknown>> {
    const qs = period ? `?period=${period}` : '';
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/analytics/pipeline${qs}`);
    return res.data;
  },
  async getAnalyticsLenders(period?: string): Promise<unknown[]> {
    const qs = period ? `?period=${period}` : '';
    const res = await request<{ success: boolean; data: unknown[] }>(`/analytics/lenders${qs}`);
    return res.data ?? [];
  },
  async getAnalyticsClients(period?: string): Promise<Record<string, unknown>> {
    const qs = period ? `?period=${period}` : '';
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/analytics/clients${qs}`);
    return res.data;
  },
  async getAnalyticsOverview(): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/analytics/overview');
    return res.data;
  },

  // Document Extraction
  async triggerExtraction(docId: string, docType?: string): Promise<{ extraction_id: string; status: string }> {
    const body = docType ? { doc_type: docType } : {};
    const res = await request<{ success: boolean; data: { extraction_id?: string; id?: string; document_id?: string; status: string } }>(`/documents/${docId}/extract`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return { extraction_id: res.data.extraction_id ?? res.data.id ?? '', status: res.data.status };
  },

  // Pipeline V2: trigger extraction for a client_documents row (the path
  // brokers actually use). Distinct from triggerExtraction() above which
  // hits /documents/:id/extract — that's the broker-side documents table
  // route. This one hits /api/extractions/trigger which works against
  // client_documents and was fixed in the previous session to actually
  // call extractClientDocument via waitUntil.
  async triggerClientExtraction(documentId: string, force?: boolean): Promise<{ document_id: string; status: string }> {
    const res = await request<{ success: boolean; data: { document_id: string; status: string } }>('/extractions/trigger', {
      method: 'POST',
      body: JSON.stringify({ document_id: documentId, force: force ?? false }),
    });
    return res.data;
  },

  // Pipeline V2: list extractions for a client. Returns the UNION of
  // client_documents and documents extractions filtered by client_id.
  async listClientExtractions(clientId: string): Promise<Array<Record<string, unknown>>> {
    const res = await request<{ success: boolean; data: Array<Record<string, unknown>> }>(`/extractions?client_id=${encodeURIComponent(clientId)}`);
    return res.data ?? [];
  },
  async getExtraction(docId: string): Promise<ExtractionResult> {
    const res = await request<{ success: boolean; data: ExtractionResult }>(`/documents/${docId}/extraction`);
    return res.data;
  },
  async retryExtraction(docId: string): Promise<ExtractionResult> {
    const res = await request<{ success: boolean; data: ExtractionResult }>(`/documents/${docId}/extraction/retry`, { method: 'POST' });
    return res.data;
  },
  async updateExtractionFields(docId: string, fields: Partial<BankStatementFields>): Promise<ExtractionResult> {
    const res = await request<{ success: boolean; data: ExtractionResult }>(`/documents/${docId}/extraction/fields`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    });
    return res.data;
  },
  async getPendingExtractions(): Promise<PendingExtraction[]> {
    const res = await request<{ success: boolean; data: PendingExtraction[] }>('/documents/extractions/pending');
    return res.data ?? [];
  },
  async validateClientDocs(clientId: string): Promise<CrossDocValidation> {
    const res = await request<{ success: boolean; data: CrossDocValidation }>(`/documents/validate/${clientId}`);
    return res.data;
  },

  // Field Mappings
  async getFieldMappings(lenderSlug: string): Promise<FieldMapping[]> {
    const res = await request<{ success: boolean; data: FieldMapping[] }>(`/admin/lenders/${lenderSlug}/field-map`);
    return res.data ?? [];
  },
  async saveFieldMappings(lenderSlug: string, mappings: FieldMapping[]): Promise<FieldMapping[]> {
    const res = await request<{ success: boolean; data: FieldMapping[] }>(`/admin/lenders/${lenderSlug}/field-map`, {
      method: 'PUT',
      body: JSON.stringify({ mappings }),
    });
    return res.data ?? [];
  },
  async testFieldMapping(lenderSlug: string, clientId: string): Promise<Record<string, string>> {
    const res = await request<{ success: boolean; data: Record<string, string> }>(`/admin/lenders/${lenderSlug}/field-map/test?client_id=${clientId}`);
    return res.data ?? {};
  },

  // Prefill
  async getPrefillLenders(): Promise<Lender[]> {
    const res = await request<{ success: boolean; data: Lender[] }>('/applications/lenders');
    return res.data ?? [];
  },
  async getAllApplicationLenders(): Promise<Lender[]> {
    const res = await request<{ success: boolean; data: Lender[] }>('/applications/lenders/all');
    return res.data ?? [];
  },
  async generatePrefill(dealId: string, lenderId: string, extraFields?: Record<string, string>): Promise<{ download_url: string; job_id: string; expires_at?: string }> {
    const res = await request<{ success: boolean; data: { download_url: string; job_id: string; expires_at?: string } }>('/applications/prefill', {
      method: 'POST',
      body: JSON.stringify({ deal_id: dealId, lender_id: lenderId, extra_fields: extraFields }),
    });
    return res.data;
  },

  // Command Center
  async getDealCommand(dealId: string): Promise<CommandPayload> {
    const res = await request<{ success: boolean; data: CommandPayload }>(`/brokers/me/deals/${dealId}/command`);
    return res.data;
  },
  async createSubmission(dealId: string, data: { lender_id: string; document_ids?: string[] }): Promise<CommandSubmission> {
    const res = await request<{ success: boolean; data: CommandSubmission }>(`/brokers/me/deals/${dealId}/submissions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.data;
  },
  async updateSubmission(dealId: string, submissionId: string, data: { status?: string; response_status?: string; response_notes?: string }): Promise<CommandSubmission> {
    const res = await request<{ success: boolean; data: CommandSubmission }>(`/brokers/me/deals/${dealId}/submissions/${submissionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return res.data;
  },
  async updateDealStage(dealId: string, stage: string): Promise<Deal> {
    const res = await request<{ success: boolean; data: Deal }>(`/brokers/me/deals/${dealId}/stage`, {
      method: 'PATCH',
      body: JSON.stringify({ stage }),
    });
    return res.data;
  },
  async getDealStatusSummary(dealId: string): Promise<any> {
    const res = await request<{ success: boolean; data: any }>(`/deals/${dealId}/status-summary`);
    return res.data;
  },
  async getAttentionRequired(): Promise<any> {
    const res = await request<{ success: boolean; data: any }>("/deals/broker/attention-required");
    return res.data;
  },

  // ── Pipeline Tiles ──
  async getDealTiles(dealId: string): Promise<Record<string, unknown>[]> {
    const res = await request<{ success: boolean; data: Record<string, unknown>[] }>(`/brokers/me/deals/${dealId}/tiles`);
    return res.data ?? [];
  },
  async createDealTile(dealId: string, data: { title: string; tile_type: string; description?: string }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/deals/${dealId}/tiles`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.data;
  },
  async updateDealTile(tileId: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/pipeline/tiles/${tileId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  // Deal Documents (bulk upload)
  async uploadDealDocumentsBulk(dealId: string, files: File[], categories?: Array<{ filename: string; category: string }>): Promise<BulkUploadResult> {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    if (categories) {
      formData.append('categories', JSON.stringify(categories));
    }
    const res = await request<{ success: boolean; data: BulkUploadResult }>(`/brokers/me/deals/${dealId}/documents/bulk`, {
      method: 'POST',
      body: formData,
    });
    return res.data;
  },
  async uploadDealZip(dealId: string, zipFile: File): Promise<ZipUploadResult> {
    const formData = new FormData();
    formData.append('file', zipFile);
    const res = await request<{ success: boolean; data: ZipUploadResult }>(`/brokers/me/deals/${dealId}/documents/upload-zip`, {
      method: 'POST',
      body: formData,
    });
    return res.data;
  },
  async getDealDocuments(dealId: string): Promise<DealDocument[]> {
    const res = await request<{ success: boolean; data: DealDocument[] }>(`/brokers/me/deals/${dealId}/documents`);
    return res.data ?? [];
  },
  async updateDealDocumentCategory(dealId: string, docId: string, category: string): Promise<DealDocument> {
    const res = await request<{ success: boolean; data: DealDocument }>(`/brokers/me/deals/${dealId}/documents/${docId}`, {
      method: 'PATCH',
      body: JSON.stringify({ category }),
    });
    return res.data;
  },
  async confirmDealDocuments(dealId: string, documents: Array<{ id: string; category: string }>): Promise<DealDocument[]> {
    const res = await request<{ success: boolean; data: DealDocument[] }>(`/brokers/me/deals/${dealId}/documents/confirm`, {
      method: 'POST',
      body: JSON.stringify({ documents }),
    });
    return res.data ?? [];
  },
  async deleteDealDocument(dealId: string, docId: string): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/deals/${dealId}/documents/${docId}`, { method: 'DELETE' });
  },

  // ── Deal Review Flow ──
  async getClientReviewStatus(clientId: string): Promise<{ deal_status: string; admin_notes: string | null; submitted_at: string | null; reviewed_at: string | null; reviewed_by: string | null; doc_count: number; can_submit: boolean }> {
    const res = await request<{ success: boolean; data: { deal_status: string; admin_notes: string | null; submitted_at: string | null; reviewed_at: string | null; reviewed_by: string | null; doc_count: number; can_submit: boolean } }>(`/brokers/me/clients/${clientId}/review-status`);
    return res.data;
  },
  async submitClientForReview(clientId: string): Promise<{ deal_status: string }> {
    const res = await request<{ success: boolean; data: { deal_status: string } }>(`/brokers/me/clients/${clientId}/submit-for-review`, { method: 'POST' });
    return res.data;
  },

  // ── Admin Deal Queue ──
  async getAdminDealQueue(params?: string): Promise<{ data: Record<string, unknown>[]; totalPages: number; pagination: { page: number; limit: number; total: number } }> {
    const qs = params ? `?${params}` : '';
    const res = await request<{ success: boolean; data: { deals: Record<string, unknown>[]; pagination: { page: number; limit: number; total: number } } }>(`/admin/deal-queue${qs}`);
    const { deals, pagination } = res.data;
    const totalPages = Math.ceil((pagination.total || 0) / (pagination.limit || 25));
    return { data: deals ?? [], totalPages, pagination };
  },
  async getAdminDealDetail(clientId: string): Promise<{ client: Record<string, unknown>; documents: Record<string, unknown>[] }> {
    const res = await request<{ success: boolean; data: { client: Record<string, unknown>; documents: Record<string, unknown>[] } }>(`/admin/deal-queue/${clientId}`);
    return res.data;
  },
  async approveDeal(clientId: string): Promise<{ deal_status: string }> {
    const res = await request<{ success: boolean; data: { deal_status: string } }>(`/admin/deal-queue/${clientId}/approve`, { method: 'POST' });
    return res.data;
  },
  async requestDealChanges(clientId: string, notes: string): Promise<{ deal_status: string }> {
    const res = await request<{ success: boolean; data: { deal_status: string } }>(`/admin/deal-queue/${clientId}/request-changes`, { method: 'POST', body: JSON.stringify({ notes }) });
    return res.data;
  },
  async rejectDeal(clientId: string, reason: string): Promise<{ deal_status: string }> {
    const res = await request<{ success: boolean; data: { deal_status: string } }>(`/admin/deal-queue/${clientId}/reject`, { method: 'POST', body: JSON.stringify({ reason }) });
    return res.data;
  },
  // ── Admin Deal Packages ──
  async getPackageLenders(): Promise<Record<string, unknown>[]> {
    const res = await request<{ success: boolean; data: Record<string, unknown>[] }>('/admin/deal-packages/lenders');
    return res.data;
  },
  async getClientDocsForPackage(clientId: string): Promise<Record<string, unknown>[]> {
    const res = await request<{ success: boolean; data: Record<string, unknown>[] }>(`/admin/deal-packages/${clientId}/documents`);
    return res.data;
  },
  async getClientSubmissions(clientId: string): Promise<Record<string, unknown>[]> {
    const res = await request<{ success: boolean; data: Record<string, unknown>[] }>(`/admin/deal-packages/${clientId}/submissions`);
    return res.data;
  },
  async requestClientDocs(clientId: string, requestedDocs?: string[]): Promise<{ token: string; upload_url: string }> {
    const res = await request<{ success: boolean; data: { token: string; upload_url: string } }>(`/brokers/me/clients/${clientId}/request-docs`, {
      method: 'POST',
      body: JSON.stringify({ requested_docs: requestedDocs }),
    });
    return res.data;
  },
  async getUploadPortal(token: string): Promise<{ client_name: string; business_name: string; broker_name: string; requested_docs: string[]; expires_at: string }> {
    const res = await request<{ success: boolean; data: { client_name: string; business_name: string; broker_name: string; requested_docs: string[]; expires_at: string } }>(`/public/upload/${token}`);
    return res.data;
  },
  async uploadViaToken(token: string, files: File[], docType?: string): Promise<{ uploaded: Array<{ id: string; file_name: string }> }> {
    const formData = new FormData();
    for (const file of files) formData.append('files', file);
    if (docType) formData.append('doc_type', docType);
    const res = await fetch(`/api/public/upload/${token}`, { method: 'POST', body: formData });
    if (!res.ok) { const err = await res.json().catch(() => ({ error: 'Upload failed' })); throw new Error((err as { error: string }).error); }
    const json = await res.json() as { success: boolean; data: { uploaded: Array<{ id: string; file_name: string }> } };
    return json.data;
  },
  async emailLenderPackage(clientId: string, lenderId: string, packageUrl: string): Promise<{ sent: boolean }> {
    const res = await request<{ success: boolean; data: { sent: boolean } }>(`/brokers/me/clients/${clientId}/email-lender-package`, {
      method: 'POST',
      body: JSON.stringify({ lender_id: lenderId, package_url: packageUrl }),
    });
    return res.data;
  },
  async generatePackage(clientId: string, lenderId: string): Promise<{ submissionId: string; packageUrl: string; lenderName: string; documentCount: number; hasFilledApplication: boolean }> {
    const res = await request<{ success: boolean; data: { submissionId: string; packageUrl: string; lenderName: string; documentCount: number; hasFilledApplication: boolean } }>(`/brokers/me/clients/${clientId}/generate-package`, { method: 'POST', body: JSON.stringify({ lenderId }) });
    return res.data;
  },
  async markSubmissionSubmitted(submissionId: string): Promise<{ status: string }> {
    const res = await request<{ success: boolean; data: { status: string } }>(`/admin/deal-packages/submissions/${submissionId}/mark-submitted`, { method: 'POST' });
    return res.data;
  },
  // ── Admin Deal Operations ─────────────────────────────────────
  async getAdminDeals(params?: string): Promise<{ data: Deal[]; totalPages: number; pagination: { page: number; limit: number; total: number } }> {
    const qs = params ? `?${params}` : '';
    const res = await request<{ success: boolean; data: { deals: Deal[]; pagination: { page: number; limit: number; total: number } } }>(`/admin/deals${qs}`);
    const { deals, pagination } = res.data;
    return { data: deals ?? [], totalPages: Math.ceil((pagination.total || 0) / (pagination.limit || 25)), pagination };
  },
  async getAdminDeal(dealId: string): Promise<Deal> {
    const res = await request<{ success: boolean; data: Deal }>(`/admin/deals/${dealId}`);
    return res.data;
  },
  async getAdminDealCommand(dealId: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/admin/deals/${dealId}/command`);
    return res.data;
  },
  async adminUpdateDeal(dealId: string, data: Record<string, unknown>): Promise<Deal> {
    const res = await request<{ success: boolean; data: Deal }>(`/admin/deals/${dealId}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async adminUpdateDealStage(dealId: string, stage: string): Promise<Deal> {
    const res = await request<{ success: boolean; data: Deal }>(`/admin/deals/${dealId}/stage`, { method: 'PATCH', body: JSON.stringify({ stage }) });
    return res.data;
  },
  // Roadmap
  async getAdminRoadmap(dealId: string): Promise<Record<string, unknown>[]> {
    const res = await request<{ success: boolean; data: Record<string, unknown>[] }>(`/admin/deals/${dealId}/roadmap`);
    return res.data ?? [];
  },
  async addAdminRoadmapItem(dealId: string, data: { text: string; due_date?: string; notes?: string }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/admin/deals/${dealId}/roadmap`, { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateAdminRoadmapItem(dealId: string, itemId: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/admin/deals/${dealId}/roadmap/${itemId}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async deleteAdminRoadmapItem(dealId: string, itemId: string): Promise<void> {
    await request<{ success: boolean }>(`/admin/deals/${dealId}/roadmap/${itemId}`, { method: 'DELETE' });
  },
  // Doc requests
  async adminRequestDoc(dealId: string, data: { doc_type: string; note?: string }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/admin/deals/${dealId}/request-doc`, { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async getAdminDocRequests(dealId: string): Promise<Record<string, unknown>[]> {
    const res = await request<{ success: boolean; data: Record<string, unknown>[] }>(`/admin/deals/${dealId}/doc-requests`);
    return res.data ?? [];
  },
  // Activity log
  async addAdminDealNote(dealId: string, description: string): Promise<void> {
    await request<{ success: boolean }>(`/admin/deals/${dealId}/activity`, { method: 'POST', body: JSON.stringify({ description }) });
  },
  // ── Quick Add Client ──
  async getActiveLenders(): Promise<Array<{ id: string; name: string; lender_type: string | null }>> {
    const res = await request<{ success: boolean; data: Array<{ id: string; name: string; lender_type: string | null }> }>('/brokers/me/clients/lenders');
    return res.data ?? [];
  },
  async quickAddClient(data: {
    business_name: string;
    owner_name?: string;
    phone?: string;
    email?: string;
    amount_requested?: number;
    // stage values must match deals.status CHECK. Legacy 'prework' and
    // 'reviewing' are accepted by the backend for backward compatibility
    // but new frontend code should send 'processing' / 'submitted'.
    stage: 'intake' | 'processing' | 'submitted' | 'funded' | 'prework' | 'reviewing';
    lenders?: Array<{ lender_id: string; status: string }>;
    amount_received?: number;
  }): Promise<{ client_id: string }> {
    const res = await request<{ success: boolean; client_id: string }>('/brokers/me/clients/quick-add', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return { client_id: res.client_id };
  },
  // ── Daily Focus ──
  async getDailyFocus(): Promise<{
    date: string;
    pending: Array<{ id: string; client_id: string; deal_id: string | null; task_type: string; title: string; description: string | null; amount: number | null; due_date: string; completed_at: string | null; rolled_from: string | null; business_name: string | null; deal_name: string | null }>;
    completed: Array<{ id: string; client_id: string; deal_id: string | null; task_type: string; title: string; description: string | null; amount: number | null; due_date: string; completed_at: string | null; rolled_from: string | null; business_name: string | null; deal_name: string | null }>;
    deal_tasks: Array<{ id: string; deal_id: string; client_id: string; task_type: string; title: string; status: string; due_at: string | null; tile_id: string | null; business_name: string | null; deal_name: string | null; created_at: string }>;
    streak: number;
  }> {
    const res = await request<{ success: boolean; data: { date: string; pending: Array<Record<string, unknown>>; completed: Array<Record<string, unknown>>; deal_tasks: Array<Record<string, unknown>>; streak: number } }>('/daily-focus');
    return res.data as ReturnType<typeof api.getDailyFocus> extends Promise<infer R> ? R : never;
  },
  async completeDailyTask(id: string): Promise<void> {
    await request<{ success: boolean }>(`/daily-focus/${id}/complete`, { method: 'POST' });
  },
  async rollDailyTask(id: string): Promise<void> {
    await request<{ success: boolean }>(`/daily-focus/${id}/roll`, { method: 'POST' });
  },
  async endOfDay(): Promise<{ rolled: number; new_due_date: string }> {
    const res = await request<{ success: boolean; data: { rolled: number; new_due_date: string } }>('/daily-focus/end-of-day', { method: 'POST' });
    return res.data;
  },
  async createDailyTask(data: { client_id: string; deal_id?: string; title: string; task_type?: string; due_date?: string }): Promise<{ id: string }> {
    const res = await request<{ success: boolean; data: { id: string } }>('/daily-focus', { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  // ── Communication Hub ──
  async sendEmailToClient(clientId: string, subject: string, body: string, options?: { deal_id?: string; reply_to_message_id?: string; thread_id?: string }): Promise<void> {
    await request<{ success: boolean }>('/communications/send-email', { method: 'POST', body: JSON.stringify({ client_id: clientId, subject, body, ...options }) });
  },
  async sendSmsToClient(clientId: string, message: string): Promise<{ sid: string }> {
    const res = await request<{ success: boolean; data: { sid: string } }>('/communications/send-sms', { method: 'POST', body: JSON.stringify({ client_id: clientId, message }) });
    return res.data;
  },
  async sendWhatsAppToClient(clientId: string, message: string): Promise<{ messageId: string }> {
    const res = await request<{ success: boolean; data: { messageId: string } }>('/communications/send-whatsapp', { method: 'POST', body: JSON.stringify({ client_id: clientId, message }) });
    return res.data;
  },

  // ── Integration Status ──
  async getIntegrationStatus(): Promise<{ google: boolean; gmail: boolean; docusign: boolean; twilio: boolean; whatsapp: boolean; telnyx: boolean; webhook: boolean }> {
    const res = await request<{ success: boolean; data: { google: boolean; gmail: boolean; docusign: boolean; twilio: boolean; whatsapp: boolean; telnyx: boolean; webhook: boolean } }>('/integrations/status');
    return res.data;
  },
  async getDocuSignStatus(): Promise<{ connected: boolean }> {
    const res = await request<{ success: boolean; data: { connected: boolean } }>('/integrations/docusign/status');
    return res.data;
  },
  async connectDocuSign(): Promise<{ url: string }> {
    const res = await request<{ success: boolean; data: { url: string } }>('/integrations/docusign/connect');
    return res.data;
  },
  async disconnectDocuSign(): Promise<void> {
    await request<{ success: boolean }>('/integrations/docusign/disconnect', { method: 'DELETE' });
  },
  async sendForSignature(documentId: string, signers: Array<{ name: string; email: string; order?: number }>): Promise<{ envelopeId: string }> {
    const res = await request<{ success: boolean; data: { envelopeId: string } }>('/integrations/docusign/send', { method: 'POST', body: JSON.stringify({ document_id: documentId, signers }) });
    return res.data;
  },
  async triggerGmailSync(): Promise<{ synced: number; errors: number }> {
    const res = await request<{ success: boolean; data: { synced: number; errors: number } }>('/integrations/gmail/sync', { method: 'POST' });
    return res.data;
  },

  // ── Client Magic Link ──
  async sendClientMagicLink(email: string): Promise<void> {
    await fetch('/api/client-auth/send-link', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
  },
  async verifyClientMagicLink(token: string): Promise<{ clientId: string }> {
    const res = await fetch(`/api/client-auth/verify/${token}`);
    if (!res.ok) throw new Error('Invalid or expired link');
    const json = await res.json() as { success: boolean; data: { clientId: string } };
    return json.data;
  },
  // ── Deal Scoring ──
  async getDealScore(dealId: string): Promise<{ score: number; tier: string; factors: Array<{ name: string; value: string; points: number; max: number }>; scored_at: string; cached?: boolean }> {
    const res = await request<{ success: boolean; data: { score: number; tier: string; factors: Array<{ name: string; value: string; points: number; max: number }>; scored_at: string; cached?: boolean } }>(`/deals/${dealId}/score`);
    return res.data;
  },
  async recalculateDealScore(dealId: string): Promise<{ score: number; tier: string; factors: Array<{ name: string; value: string; points: number; max: number }> }> {
    const res = await request<{ success: boolean; data: { score: number; tier: string; factors: Array<{ name: string; value: string; points: number; max: number }> } }>(`/deals/${dealId}/score`, { method: 'POST' });
    return res.data;
  },
  async getLenderRecommendations(dealId: string): Promise<{ recommended: Array<Record<string, unknown>>; others: Array<Record<string, unknown>>; total: number }> {
    const res = await request<{ success: boolean; data: { recommended: Array<Record<string, unknown>>; others: Array<Record<string, unknown>>; total: number } }>(`/deals/${dealId}/lender-recommendations`);
    return res.data;
  },

  // ── Teams ──
  async getTeam(): Promise<{ team: Record<string, unknown> | null; members: Array<Record<string, unknown>>; my_role: string } | null> {
    const res = await request<{ success: boolean; data: { team: Record<string, unknown> | null; members: Array<Record<string, unknown>>; my_role: string } | null }>('/teams/team');
    return res.data;
  },
  async createTeam(name: string): Promise<{ id: string }> {
    const res = await request<{ success: boolean; data: { id: string } }>('/teams/team', { method: 'POST', body: JSON.stringify({ name }) });
    return res.data;
  },
  async addTeamMember(userId: string, role: string): Promise<{ id: string }> {
    const res = await request<{ success: boolean; data: { id: string } }>('/teams/team/members', { method: 'POST', body: JSON.stringify({ user_id: userId, role }) });
    return res.data;
  },
  async updateTeamMemberRole(memberId: string, role: string): Promise<void> {
    await request<{ success: boolean }>(`/teams/team/members/${memberId}`, { method: 'PATCH', body: JSON.stringify({ role }) });
  },
  async removeTeamMember(memberId: string): Promise<void> {
    await request<{ success: boolean }>(`/teams/team/members/${memberId}`, { method: 'DELETE' });
  },
  async getClientAssignments(clientId: string): Promise<Array<Record<string, unknown>>> {
    const res = await request<{ success: boolean; data: Array<Record<string, unknown>> }>(`/teams/clients/${clientId}/assignments`);
    return res.data;
  },
  async assignClientUser(clientId: string, userId: string, role?: string): Promise<{ id: string }> {
    const res = await request<{ success: boolean; data: { id: string } }>(`/teams/clients/${clientId}/assignments`, { method: 'POST', body: JSON.stringify({ user_id: userId, role: role ?? 'collaborator' }) });
    return res.data;
  },
  async removeClientAssignment(clientId: string, assignmentId: string): Promise<void> {
    await request<{ success: boolean }>(`/teams/clients/${clientId}/assignments/${assignmentId}`, { method: 'DELETE' });
  },

  // ── API Keys ──
  async getApiKeys(): Promise<Array<Record<string, unknown>>> {
    const res = await request<{ success: boolean; data: Array<Record<string, unknown>> }>('/api-keys');
    return res.data;
  },
  async createApiKey(name: string, scopes?: string[]): Promise<{ id: string; key: string; key_prefix: string }> {
    const res = await request<{ success: boolean; data: { id: string; key: string; key_prefix: string } }>('/api-keys', { method: 'POST', body: JSON.stringify({ name, scopes: scopes ?? ['read'] }) });
    return res.data;
  },
  async revokeApiKey(keyId: string): Promise<void> {
    await request<{ success: boolean }>(`/api-keys/${keyId}`, { method: 'DELETE' });
  },

  // ── Outgoing Webhooks ──
  async getOutgoingWebhooks(): Promise<Array<Record<string, unknown>>> {
    const res = await request<{ success: boolean; data: Array<Record<string, unknown>> }>('/outgoing-webhooks');
    return res.data;
  },
  async createOutgoingWebhook(url: string, event: string): Promise<{ id: string; secret: string }> {
    const res = await request<{ success: boolean; data: { id: string; secret: string } }>('/outgoing-webhooks', { method: 'POST', body: JSON.stringify({ url, event }) });
    return res.data;
  },
  async toggleOutgoingWebhook(webhookId: string): Promise<void> {
    await request<{ success: boolean }>(`/outgoing-webhooks/${webhookId}`, { method: 'PATCH' });
  },
  async deleteOutgoingWebhook(webhookId: string): Promise<void> {
    await request<{ success: boolean }>(`/outgoing-webhooks/${webhookId}`, { method: 'DELETE' });
  },
  async getWebhookDeliveries(webhookId: string): Promise<Array<Record<string, unknown>>> {
    const res = await request<{ success: boolean; data: Array<Record<string, unknown>> }>(`/outgoing-webhooks/${webhookId}/deliveries`);
    return res.data;
  },
  async getWebhookEvents(): Promise<string[]> {
    const res = await request<{ success: boolean; data: string[] }>('/outgoing-webhooks/events');
    return res.data;
  },

  // ── Advanced Analytics ──
  async getVelocity(): Promise<{ velocity: Record<string, unknown>; trend: Array<Record<string, unknown>> }> {
    const res = await request<{ success: boolean; data: { velocity: Record<string, unknown>; trend: Array<Record<string, unknown>> } }>('/analytics/velocity');
    return res.data;
  },
  async getForecast(): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/analytics/forecast');
    return res.data;
  },
  // ── Client Portal: Doc Requirements ──
  async getClientDealDocRequirements(dealId: string): Promise<{ requirements: Array<Record<string, unknown>>; readiness: { total: number; required: number; fulfilled: number; pct: number } }> {
    const res = await request<{ success: boolean; data: { requirements: Array<Record<string, unknown>>; readiness: { total: number; required: number; fulfilled: number; pct: number } } }>(`/clients/me/deals/${dealId}/doc-requirements`);
    return res.data;
  },
  async uploadToRequirement(dealId: string, reqId: string, file: File): Promise<{ document_id: string; requirement_id: string }> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`/api/clients/me/deals/${dealId}/doc-requirements/${reqId}/upload`, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw new Error('Upload failed');
    const json = await res.json() as { success: boolean; data: { document_id: string; requirement_id: string } };
    return json.data;
  },

  // ── Client Portal: Messages ──
  async getClientMessages(): Promise<Array<Record<string, unknown>>> {
    const res = await request<{ success: boolean; data: Array<Record<string, unknown>> }>('/clients/me/messages');
    return res.data;
  },
  async sendClientMessage(content: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/clients/me/messages', { method: 'POST', body: JSON.stringify({ content }) });
    return res.data;
  },
  async getClientUnreadCount(): Promise<number> {
    const res = await request<{ success: boolean; data: { count: number } }>('/clients/me/messages/unread');
    return res.data.count;
  },

  // ── Client Portal: Credit ──
  async getClientCredit(): Promise<{ credit_score: number | null; score_trend: string; utilization_pct: number | null; total_debt: number | null; total_credit_limit: number | null; last_pulled_at: string | null; history: Array<{ credit_score: number | null; pulled_at: string }> }> {
    const res = await request<{ success: boolean; data: { credit_score: number | null; score_trend: string; utilization_pct: number | null; total_debt: number | null; total_credit_limit: number | null; last_pulled_at: string | null; history: Array<{ credit_score: number | null; pulled_at: string }> } }>('/clients/me/credit');
    return res.data;
  },

  // ── Broker: Deal Doc Requirements ──
  async getDealDocRequirements(dealId: string): Promise<{ requirements: Array<Record<string, unknown>>; readiness: Record<string, unknown> }> {
    const res = await request<{ success: boolean; data: { requirements: Array<Record<string, unknown>>; readiness: Record<string, unknown> } }>(`/deals/${dealId}/doc-requirements`);
    return res.data;
  },
  async addDealDocRequirement(dealId: string, data: { doc_type: string; custom_label?: string; required?: boolean; notes?: string }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/deals/${dealId}/doc-requirements`, { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateDealDocRequirement(dealId: string, reqId: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/deals/${dealId}/doc-requirements/${reqId}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async deleteDealDocRequirement(dealId: string, reqId: string): Promise<void> {
    await request<{ success: boolean }>(`/deals/${dealId}/doc-requirements/${reqId}`, { method: 'DELETE' });
  },
  async applyLenderTemplate(dealId: string, lenderId: string): Promise<Array<Record<string, unknown>>> {
    const res = await request<{ success: boolean; data: Array<Record<string, unknown>> }>(`/deals/${dealId}/apply-lender-template`, { method: 'POST', body: JSON.stringify({ lender_id: lenderId }) });
    return res.data;
  },

  // ── Portal Studio: COPY overrides (portal_copy) ──
  async getPortalCopy(): Promise<Record<string, string>> {
    const res = await request<{ success: boolean; data: Record<string, string> }>('/brokers/me/portal-copy');
    return res.data ?? {};
  },
  async updatePortalCopy(copy: Record<string, string>): Promise<void> {
    await request<{ success: boolean }>('/brokers/me/portal-copy', { method: 'PUT', body: JSON.stringify({ copy }) });
  },

  // ── Portal Studio: broker DEFAULT doc-requirement catalog ──
  async getPortalDocCatalog(): Promise<Array<Record<string, unknown>>> {
    const res = await request<{ success: boolean; data: Array<Record<string, unknown>> }>('/brokers/me/portal/doc-catalog');
    return res.data ?? [];
  },
  async addPortalDocCatalog(data: { label: string; doc_type?: string; input_type?: string; sample_hint?: string; layer?: number; optional?: boolean }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/brokers/me/portal/doc-catalog', { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updatePortalDocCatalog(id: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/portal/doc-catalog/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async deletePortalDocCatalog(id: string): Promise<void> {
    await request<{ success: boolean }>(`/brokers/me/portal/doc-catalog/${id}`, { method: 'DELETE' });
  },
  // ── Bulk CSV Import ──
  async importClients(clients: Array<Record<string, unknown>>, skipDuplicates?: boolean): Promise<{ created: number; updated: number; skipped: number; errors: Array<{ row: number; email: string; error: string }> }> {
    const res = await request<{ success: boolean; data: { created: number; updated: number; skipped: number; errors: Array<{ row: number; email: string; error: string }> } }>('/brokers/me/clients/import', {
      method: 'POST',
      body: JSON.stringify({ clients, skip_duplicates: skipDuplicates ?? true }),
    });
    return res.data;
  },

  // ── Broker Dashboard ──
  async getBrokerDashboard(): Promise<{
    overdue: Array<Record<string, unknown>>;
    today: Array<Record<string, unknown>>;
    upcoming: Array<Record<string, unknown>>;
    stats: {
      total_pipeline_value: number;
      pipeline_value: number;
      pipeline_deals: number;
      funded_mtd: number;
      funded_mtd_deals: number;
      funded_ytd: number;
      funded_ytd_deals: number;
      commission_mtd: number;
      commission_ytd: number;
      conversion_rate: number;
    };
  }> {
    const res = await request<{ success: boolean; data: { overdue: Array<Record<string, unknown>>; today: Array<Record<string, unknown>>; upcoming: Array<Record<string, unknown>>; stats: { total_pipeline_value: number; pipeline_value: number; pipeline_deals: number; funded_mtd: number; funded_mtd_deals: number; funded_ytd: number; funded_ytd_deals: number; commission_mtd: number; commission_ytd: number; conversion_rate: number } } }>('/broker-dashboard');
    return res.data;
  },
  async snoozeTask(taskId: string, until: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/broker-dashboard/${taskId}/snooze`, { method: 'PATCH', body: JSON.stringify({ until }) });
    return res.data;
  },
  async createQuickTask(data: { title: string; client_id?: string; deal_id?: string; due_at?: string; notes?: string }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>('/broker-dashboard/quick-task', { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },

  // ── Banker Contacts ──
  async getBankerContacts(): Promise<Array<Record<string, unknown>>> {
    const res = await request<{ success: boolean; data: Array<Record<string, unknown>> }>('/banker-contacts/contacts');
    return res.data;
  },
  async getLenderBankerContacts(lenderId: string): Promise<Array<Record<string, unknown>>> {
    const res = await request<{ success: boolean; data: Array<Record<string, unknown>> }>(`/banker-contacts/lenders/${lenderId}/contacts`);
    return res.data;
  },
  async createBankerContact(lenderId: string, data: { name: string; title?: string; email?: string; phone?: string; notes?: string; preferred_contact_method?: string; response_speed?: string }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/banker-contacts/lenders/${lenderId}/contacts`, { method: 'POST', body: JSON.stringify(data) });
    return res.data;
  },
  async updateBankerContact(id: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/banker-contacts/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },
  async deleteBankerContact(id: string): Promise<void> {
    await request<{ success: boolean }>(`/banker-contacts/contacts/${id}`, { method: 'DELETE' });
  },

  // ── Deal Results ──
  async updateDealOutcome(dealId: string, data: { outcome: string; funded_amount?: number; commission_percent?: number; commission_amount?: number; funded_date?: string; referred_to?: string; outcome_notes?: string }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/brokers/me/deals/${dealId}`, { method: 'PATCH', body: JSON.stringify(data) });
    return res.data;
  },

  // ─────────────────────────────────────────────────────────────────────
  // Pipeline V2 (migration 0120)
  // Routes mounted under /api/deals/:dealId/{cards,fees}, /api/playbooks,
  // and /api/experian/clients/:clientId. See:
  //   - apps/api/src/routes/deal-cards.ts
  //   - apps/api/src/routes/deal-fees.ts
  //   - apps/api/src/routes/playbooks.ts
  //   - apps/api/src/routes/experian.ts (PATCH /clients/:clientId)
  //
  // Each method matches the route's request shape and unwraps the
  // { success, data } envelope at the call site so consumers get .data directly.
  // ─────────────────────────────────────────────────────────────────────

  // ── Deal Fees (aggregated read + per-product rate edits) ──
  async getDealFees(dealId: string): Promise<{
    loc_fee_rate: number;
    card_fee_rate: number;
    lenders: Array<Record<string, unknown> & { commission: Record<string, unknown> | null }>;
    cards: Array<Record<string, unknown> & { commission: Record<string, unknown> | null }>;
    total_earned: number;
    total_pending: number;
  }> {
    const res = await request<{ success: boolean; data: {
      loc_fee_rate: number;
      card_fee_rate: number;
      lenders: Array<Record<string, unknown> & { commission: Record<string, unknown> | null }>;
      cards: Array<Record<string, unknown> & { commission: Record<string, unknown> | null }>;
      total_earned: number;
      total_pending: number;
    } }>(`/deals/${dealId}/fees`);
    return res.data;
  },

  async updateLenderFeeRate(dealId: string, lenderId: string, feeRate: number): Promise<{ id: string; fee_rate: number }> {
    const res = await request<{ success: boolean; data: { id: string; fee_rate: number } }>(
      `/deals/${dealId}/fees/lender/${lenderId}`,
      { method: 'PATCH', body: JSON.stringify({ fee_rate: feeRate }) },
    );
    return res.data;
  },

  async updateCardFeeRate(dealId: string, cardId: string, feeRate: number): Promise<{ id: string; fee_rate: number }> {
    const res = await request<{ success: boolean; data: { id: string; fee_rate: number } }>(
      `/deals/${dealId}/fees/card/${cardId}`,
      { method: 'PATCH', body: JSON.stringify({ fee_rate: feeRate }) },
    );
    return res.data;
  },

  // ── Deal Cards (full CRUD) ──
  async listDealCards(dealId: string): Promise<Array<Record<string, unknown>>> {
    const res = await request<{ success: boolean; data: Array<Record<string, unknown>> }>(`/deals/${dealId}/cards`);
    return res.data;
  },

  async createDealCard(dealId: string, data: { card_name: string; sort_order?: number; fee_rate?: number; notes?: string }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(
      `/deals/${dealId}/cards`,
      { method: 'POST', body: JSON.stringify(data) },
    );
    return res.data;
  },

  async updateDealCard(dealId: string, cardId: string, data: {
    card_name?: string;
    sort_order?: number;
    status?: 'queued' | 'applied' | 'approved' | 'declined' | 'confirmed';
    approved_limit?: number;
    funded_confirmed?: boolean;
    fee_rate?: number;
    notes?: string | null;
  }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(
      `/deals/${dealId}/cards/${cardId}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
    return res.data;
  },

  async deleteDealCard(dealId: string, cardId: string): Promise<void> {
    await request<{ success: boolean }>(`/deals/${dealId}/cards/${cardId}`, { method: 'DELETE' });
  },

  // ── Workflow Playbooks (Pipeline V2 — distinct from /broker/playbooks
  // which is the legacy roadmap_templates UI for pipeline templates) ──
  async listPlaybooks(): Promise<{
    grouped: Record<string, Array<Record<string, unknown>>>;
    total: number;
  }> {
    const res = await request<{ success: boolean; data: {
      grouped: Record<string, Array<Record<string, unknown>>>;
      total: number;
    } }>('/playbooks');
    return res.data;
  },

  async getPlaybook(id: string): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(`/playbooks/${id}`);
    return res.data;
  },

  async createPlaybook(data: {
    name: string;
    trigger: string;
    tasks: Array<{
      title: string;
      task_type: string;
      channel: 'email' | 'sms' | 'phone' | 'manual';
      due_offset_days: number;
      description: string;
    }>;
  }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(
      '/playbooks',
      { method: 'POST', body: JSON.stringify(data) },
    );
    return res.data;
  },

  async updatePlaybook(id: string, data: {
    name?: string;
    trigger?: string;
    is_active?: number;
    tasks?: Array<{
      title: string;
      task_type: string;
      channel: 'email' | 'sms' | 'phone' | 'manual';
      due_offset_days: number;
      description: string;
    }>;
  }): Promise<Record<string, unknown>> {
    const res = await request<{ success: boolean; data: Record<string, unknown> }>(
      `/playbooks/${id}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
    return res.data;
  },

  async deactivatePlaybook(id: string): Promise<void> {
    await request<{ success: boolean }>(`/playbooks/${id}/deactivate`, { method: 'PATCH' });
  },

  async firePlaybook(id: string, dealId: string): Promise<{ tasks_inserted: number }> {
    const res = await request<{ success: boolean; data: { tasks_inserted: number } }>(
      `/playbooks/${id}/fire/${dealId}`,
      { method: 'POST' },
    );
    return res.data;
  },

  // ── Experian status lifecycle (Pipeline V2) ──
  // Distinct from credit_snapshots (the daily cron data). This is the
  // request-tracking lifecycle: did we ask the client? Did they complete?
  async updateClientExperian(clientId: string, data: {
    status: 'unrequested' | 'requested' | 'received';
    notes?: string;
  }): Promise<{ client_id: string; experian_status: string; fired_against_deal: string | null }> {
    const res = await request<{ success: boolean; data: {
      client_id: string;
      experian_status: string;
      fired_against_deal: string | null;
    } }>(
      `/experian/clients/${clientId}`,
      { method: 'PATCH', body: JSON.stringify(data) },
    );
    return res.data;
  },

} as const;

export default api;
export { ApiClientError, request };
